import { useCallback, useEffect, useMemo } from "react";

import { useUserPreferences } from "@pm2/api";
import { useScrollDirectionClass } from "@pm2/react-utils";
import {
  makeSitemapStore,
  pm2SitemapStore,
  type PM2SitemapState,
} from "@pm2/sitemap-utils";
import type { PM2ConfigState } from "@pm2/store";
import { pm2ConfigStore, makePm2ConfigStore } from "@pm2/store";
import { NProgress, nProgressStore } from "@pm2/ui-components";
import {
  FloatingButtons,
  Header,
  PostPageRouterPlaceholder,
  PrePageRouterPlaceholder,
} from "@pm2/ui-layout";
import { PrinterRoot } from "@provider/print";
import {
  mapPollySpeed,
  SpeechConfigProvider,
} from "@provider/speech-synthesis";
import type { SpeechConfig } from "@provider/speech-synthesis";
import { TanstackQueryProvider } from "@provider/tanstack";
import { resets, useVhUnit } from "@ui/css";
import { UIKitNotifierProvider, configureTooltipContainer } from "@ui/kit";
import { isPlatformNodeJS } from "@utils/common";
import { useScrollbarProperties, useTypekitReady } from "@utils/react";

import { UseCourseRevisionSideEffects } from "./core/config.course-revisions";
import { AppContextProvider } from "./core/provider.app-context";
import { ContentEnginePrinterProvider } from "./core/provider.content-engine-printers";
import { CourseBuilderStoreProvider } from "./core/provider.course-builder";
import { useAppPropsRevalidation } from "./core/provider.pm2-stores";
import { useTracking } from "./hooks/tracking";
import { PageRouter } from "./pages";
import { SkipLink } from "./pages/atom.skip-link";

export type AppProps = {
  configState: PM2ConfigState;
  sitemapState: PM2SitemapState;
};

const MINIMUM_NPROGRESS_DURATION = 650;

const notifierContainers = {
  toasts: !isPlatformNodeJS()
    ? document.querySelector<HTMLElement>("#snackbar-wrapper")
    : undefined,
};

if (!isPlatformNodeJS()) {
  configureTooltipContainer(
    document.querySelector<HTMLElement>("#tooltip-wrapper") ?? undefined,
  );
}

export const App: React.FC<AppProps & { initialPath: string }> = (props) => {
  const configStore = useMemo(
    () => makePm2ConfigStore(props.configState),
    [props.configState],
  );

  const sitemapStore = useMemo(
    () => makeSitemapStore(props.sitemapState),
    [props.sitemapState],
  );

  return (
    <pm2ConfigStore.Provider store={configStore}>
      <pm2SitemapStore.Provider store={sitemapStore}>
        <TanstackQueryProvider>
          <AppContextProvider>
            <ContentEnginePrinterProvider>
              <CourseBuilderStoreProvider>
                <AppInner initialPath={props.initialPath} />
              </CourseBuilderStoreProvider>
            </ContentEnginePrinterProvider>
          </AppContextProvider>
        </TanstackQueryProvider>
      </pm2SitemapStore.Provider>
    </pm2ConfigStore.Provider>
  );
};

function AppInner(props: { initialPath: string }): React.JSX.Element {
  useAppPropsRevalidation(props.initialPath);
  useTypekitReady();
  useTracking();
  useScrollDirectionClass();
  useScrollbarProperties();
  useVhUnit();

  const onNavigationStart = useCallback(() => {
    nProgressStore.dispatch(nProgressStore.actions.enable());
  }, []);

  const onNavigationEnd = useCallback(() => {
    nProgressStore.dispatch(nProgressStore.actions.disable());
  }, []);

  const userPreferences = useUserPreferences();

  const speechEnabled = pm2ConfigStore.useSelector(
    (state) => state.config.settings.syntheticSpeech?.enabled ?? false,
  );
  const speechKaraoke = pm2ConfigStore.useSelector(
    (state) => state.config.settings.syntheticSpeech?.highlight ?? false,
  );
  const speechLatexPrinter = pm2ConfigStore.useSelector(
    (state) => state.config.settings.syntheticSpeech?.latex,
  );
  const speechPrimaryLanguage = pm2ConfigStore.useSelector(
    (state) => state.config.settings.syntheticSpeech?.language,
  );
  const speechSecondaryLanguage = pm2ConfigStore.useSelector(
    (state) => state.config.settings.syntheticSpeech?.secondaryLanguage,
  );

  const speechConfig = useMemo(
    () =>
      ({
        enabled: speechEnabled,
        karaoke: speechKaraoke,
        latexPrinter: speechLatexPrinter,
        speed: mapPollySpeed(userPreferences.speechSynthesis.speed),
        gender: userPreferences.speechSynthesis.gender,
        languages: [speechPrimaryLanguage, speechSecondaryLanguage],
      }) satisfies SpeechConfig,
    [
      speechEnabled,
      speechKaraoke,
      speechLatexPrinter,
      speechPrimaryLanguage,
      speechSecondaryLanguage,
      userPreferences.speechSynthesis.gender,
      userPreferences.speechSynthesis.speed,
    ],
  );

  useEffect(() => {
    document.body.setAttribute("data-font-variant", userPreferences.font);
  }, [userPreferences.font]);

  return (
    <SpeechConfigProvider config={speechConfig}>
      <UIKitNotifierProvider containers={notifierContainers}>
        <div className={resets.rootCls}>
          <NProgress minDuration={MINIMUM_NPROGRESS_DURATION} />

          <SkipLink />
          <Header />
          <FloatingButtons />

          <PrePageRouterPlaceholder />
          <PageRouter
            onNavigationStart={onNavigationStart}
            onNavigationEnd={onNavigationEnd}
          />
          <PostPageRouterPlaceholder />

          <UseCourseRevisionSideEffects />

          <PrinterRoot />
        </div>
      </UIKitNotifierProvider>
    </SpeechConfigProvider>
  );
}
